/**
 * Rental request screen (§7.2, FR-4.2).
 *
 * The disclosure is the point of this screen. It is:
 *  - rendered in full, verbatim from RENTAL_DISCLOSURE in @pm/shared
 *  - non-dismissible: there is no close control and no collapse
 *  - acknowledged by an explicit action, not a pre-checked box and not a link to
 *    terms
 *  - a hard gate: Request cannot be submitted before it is acknowledged, and the
 *    server independently requires `disclosure_acknowledged: true`
 *
 * Nothing on this screen uses transactional vocabulary, because the platform
 * processes no rental transaction (§7.5, §9.4).
 */

import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { RENTAL_DISCLOSURE } from '@pm/shared';

import { useApi, useMutation } from '../../hooks/useApi';
import type { Listing, Rental } from '../../api/types';
import { Card, Field, Loading } from '../../components/ui';
import { ErrorView, InlineError } from '../../components/ErrorView';
import { amountLabel, localDateTime, periodLabel } from '../../lib/format';
import { ASKING_LABEL, OWNER_LABEL, REQUEST_LABEL, REQUEST_LABEL_LONG } from '../../lib/vocabulary';
import { routes } from '../../api/routes';

const HOURS_PER_UNIT: Record<string, number> = { day: 24, week: 24 * 7, month: 24 * 30 };

export function RentalRequest() {
  const { listingId = '' } = useParams();
  const nav = useNavigate();
  const listing = useApi<Listing>(routes.listings.one(listingId));
  const [periods, setPeriods] = useState(1);
  const [startAt, setStartAt] = useState('');
  const [acknowledged, setAcknowledged] = useState(false);

  const request = useMutation<Record<string, unknown>, Rental>(routes.rentals.collection, { idempotent: true });

  const l = listing.data;

  const endAt = useMemo(() => {
    if (!l || !startAt) return null;
    const hours = (HOURS_PER_UNIT[l.period_unit] ?? 24) * l.period_count * periods;
    return new Date(new Date(startAt).getTime() + hours * 3600_000).toISOString();
  }, [l, startAt, periods]);

  if (listing.initialLoading) return <Loading label="Loading the listing" />;
  if (listing.problem) return <ErrorView problem={listing.problem} onRetry={listing.reload} />;
  if (!l) return null;

  const askingForPeriods = { amount: l.asking.amount * periods, currency: l.asking.currency };

  return (
    <div className="narrow rental-request">
      <header className="page-head">
        <h1>{REQUEST_LABEL_LONG}</h1>
        <p className="muted">
          {l.bot_name} · {OWNER_LABEL} @{l.owner_handle}
        </p>
      </header>

      <Card title="What you are asking for">
        <Field id="rr-start" label="Start">
          <input
            id="rr-start"
            className="input"
            type="datetime-local"
            required
            value={startAt}
            onChange={(e) => setStartAt(e.target.value)}
          />
        </Field>
        <Field
          id="rr-periods"
          label={`How many × ${periodLabel(l.period_count, l.period_unit)}`}
          hint={`This listing allows ${l.min_periods} to ${l.max_periods}.`}
        >
          <input
            id="rr-periods"
            className="input input-narrow"
            type="number"
            min={l.min_periods}
            max={l.max_periods}
            value={periods}
            onChange={(e) => setPeriods(Number(e.target.value))}
          />
        </Field>
        <p>
          {ASKING_LABEL}: <strong>{amountLabel(askingForPeriods)}</strong> for{' '}
          {periods} × {periodLabel(l.period_count, l.period_unit)}.
        </p>
        {endAt ? <p className="muted">Ends {localDateTime(endAt)}.</p> : null}

        <h3>Permissions you would hold</h3>
        <ul className="perm-chips">
          {l.granted_permissions.map((p) => (
            <li key={p} className="chip chip-static">
              {p.replace(/_/g, ' ')}
            </li>
          ))}
        </ul>
        {l.terms ? (
          <>
            <h3>{OWNER_LABEL}&rsquo;s terms</h3>
            <p className="preview">{l.terms}</p>
          </>
        ) : null}
      </Card>

      {/* FR-4.2. Non-dismissible: no close control exists here by design. */}
      <section className="disclosure disclosure-required" aria-labelledby="rd-heading">
        <h2 id="rd-heading">{RENTAL_DISCLOSURE.heading}</h2>
        <p>{RENTAL_DISCLOSURE.intro}</p>
        <p>{RENTAL_DISCLOSURE.leadIn}</p>
        <ul>
          {RENTAL_DISCLOSURE.bullets.map((b) => (
            <li key={b}>{b}</li>
          ))}
        </ul>
        <p>{RENTAL_DISCLOSURE.whatWeDo}</p>

        <button
          type="button"
          className={`btn ${acknowledged ? 'btn-ghost' : 'btn-primary'}`}
          aria-pressed={acknowledged}
          onClick={() => setAcknowledged((v) => !v)}
        >
          {acknowledged ? `Acknowledged — ${RENTAL_DISCLOSURE.acknowledgeLabel}` : RENTAL_DISCLOSURE.acknowledgeLabel}
        </button>
      </section>

      <Card>
        {request.problem ? <InlineError problem={request.problem} /> : null}
        <button
          type="button"
          className="btn btn-primary"
          disabled={!acknowledged || !startAt || request.pending}
          onClick={async () => {
            const created = await request.run({
              listing_id: l.id,
              period_count: periods,
              start_at: new Date(startAt).toISOString(),
              disclosure_acknowledged: true,
            });
            if (created) nav(`/rentals/${created.id}/console`);
          }}
        >
          {request.pending ? 'Sending…' : REQUEST_LABEL}
        </button>
        {!acknowledged ? (
          <p className="field-hint">
            Read the note above and acknowledge it before sending the request.
          </p>
        ) : null}
        <p className="muted">
          The {OWNER_LABEL.toLowerCase()} decides whether to accept. Nothing happens
          until they do.
        </p>
      </Card>
    </div>
  );
}
